import { useState } from "react";
import { formatTimestamp, formatDateTime } from "@/lib/date-utils";
import { useOutletContext, Navigate, useParams, Link } from "react-router-dom";
import { CardContent } from "@/components/ui/card";
import { Button } from "@satyrsmc/shared/components";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { UserStatus, UserType } from "@satyrsmc/shared/lib/enums";
import { trpc } from "@/trpc";
import { STATUS_LABELS, TYPE_LABELS } from "@/components/users/userDisplay";
import type { ContactDetailContext } from "../ContactDetailPage";

export function ContactUserTab() {
  const { id } = useParams<{ id: string }>();
  const { userData, registrationData } = useOutletContext<ContactDetailContext>();

  if (userData === undefined || registrationData === undefined) return null;
  if (userData === null && registrationData === null)
    return <Navigate to={`/admin/contacts/${id}`} replace />;

  if (!userData && registrationData) {
    return (
      <CardContent className="space-y-4">
        <div>
          <p className="text-sm font-semibold">Invite Pending</p>
          <p className="mt-1 text-sm text-muted-foreground">
            An invitation was sent to <strong>{registrationData.email}</strong>
            {registrationData.created_at
              ? ` on ${formatTimestamp(registrationData.created_at)}`
              : ""}
            .
          </p>
          <p className="mt-1 text-sm text-muted-foreground">
            Once the recipient completes signup, their account will appear here.
          </p>
        </div>
      </CardContent>
    );
  }

  return <ContactUserTabContent userData={userData!} />;
}

function ContactUserTabContent({
  userData,
}: {
  userData: NonNullable<ContactDetailContext["userData"]>;
}) {
  const utils = trpc.useUtils();

  const invalidateUser = () => {
    utils.admin.users.getByContactId.invalidate({ contact_id: userData.contact_id });
    utils.admin.users.get.invalidate({ id: userData.id });
  };

  const updateStatus = trpc.admin.users.updateStatus.useMutation({ onSuccess: invalidateUser });
  const updateType = trpc.admin.users.updateType.useMutation({ onSuccess: invalidateUser });
  const addNote = trpc.admin.users.addNote.useMutation({ onSuccess: invalidateUser });

  const [noteText, setNoteText] = useState("");

  return (
    <CardContent className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2">
        <div>
          <p className="text-sm font-medium text-muted-foreground">Username</p>
          <p className="font-mono">{userData.username}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">Full profile</p>
          {userData.user_status === UserStatus.Active ? (
            <Link
              to={`/profile/${userData.username}`}
              className="text-primary hover:underline text-sm"
            >
              View full profile →
            </Link>
          ) : (
            <p className="text-sm text-muted-foreground">—</p>
          )}
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">Last login</p>
          <p>{userData.last_login ? formatDateTime(userData.last_login) : "Never"}</p>
        </div>
        <div>
          <p className="text-sm font-medium text-muted-foreground">Account created</p>
          <p>{userData.created_at ? formatTimestamp(userData.created_at) : "—"}</p>
        </div>
      </div>

      <div className="grid gap-4 sm:grid-cols-2 max-w-sm">
        <div className="space-y-1.5">
          <Label>Role</Label>
          <Select
            value={userData.user_type}
            onValueChange={(val) =>
              updateType.mutate({ id: userData.id, user_type: val as UserType })
            }
            disabled={updateType.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(UserType).map((t) => (
                <SelectItem key={t} value={t}>
                  {TYPE_LABELS[t]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        <div className="space-y-1.5">
          <Label>Status</Label>
          <Select
            value={userData.user_status}
            onValueChange={(val) =>
              updateStatus.mutate({ id: userData.id, user_status: val as UserStatus })
            }
            disabled={updateStatus.isPending}
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {Object.values(UserStatus).map((s) => (
                <SelectItem key={s} value={s}>
                  {STATUS_LABELS[s]}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="space-y-2 max-w-sm">
        <Label>Admin note</Label>
        {userData.admin_note && (
          <div className="rounded-md bg-muted p-3 text-sm">{userData.admin_note}</div>
        )}
        <Textarea
          placeholder="Add a note about this user..."
          value={noteText}
          onChange={(e) => setNoteText(e.target.value)}
          rows={3}
        />
        <Button
          size="sm"
          disabled={!noteText.trim() || addNote.isPending}
          onClick={() =>
            addNote.mutate(
              { id: userData.id, admin_note: noteText.trim() },
              { onSuccess: () => setNoteText("") },
            )
          }
        >
          {addNote.isPending ? "Saving..." : "Save Note"}
        </Button>
      </div>
    </CardContent>
  );
}
